import { memo, useCallback, useMemo } from "react";

import type { ForNodeType } from "@/components/features/_model/types";

import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { RadioGroupField } from "@/components/ui/form/fields/radio-field/radio-group";

import * as Constants from "./constants";
import { FormFieldLayout } from "../../layouts";

type Props = {
	// ディスカウント上限金額
	discountPriceTotalLimitAmount?: ForNodeType<number>;
	// ディスカウント上限金額を設定しない
	onUnSettingDiscountPriceTotalLimit: () => void;
	// ディスカウント上限金額の変更
	onChangeDiscountPriceTotalLimit: (amount: number) => void;
};

export const PriceTotalLimitAmountFormFieldComposition = memo(
	({ discountPriceTotalLimitAmount, onUnSettingDiscountPriceTotalLimit, onChangeDiscountPriceTotalLimit }: Props) => {
		const handleChangeDiscountPriceTotalLimit = useCallback(
			(value: string) => {
				// ディスカウント上限金額を設定しない
				if (value === "none") {
					onUnSettingDiscountPriceTotalLimit();
					return;
				}

				// ディスカウント上限金額の変更
				onChangeDiscountPriceTotalLimit(0);
			},
			[onChangeDiscountPriceTotalLimit, onUnSettingDiscountPriceTotalLimit],
		);
		/**
		 * 上限金額の変更
		 */
		const handleChangeDiscountPriceTotalLimitAmount = useCallback(
			(amount?: number) => {
				onChangeDiscountPriceTotalLimit(amount!); // 未入力（undefined）の入力を一時的に許容する。許容することでzodによるバリデーションエラーを出している。
			},
			[onChangeDiscountPriceTotalLimit],
		);

		/**
		 * チェックボックスのラベル
		 */
		const discountPriceTotalLimitAmountRadioValue = useMemo(
			() => (discountPriceTotalLimitAmount !== undefined ? "setting" : "none"),
			[discountPriceTotalLimitAmount],
		);
		/**
		 * 表示制御
		 */
		const canShowDiscountPriceTotalLimitAmount = useMemo(
			() => discountPriceTotalLimitAmountRadioValue === "setting",
			[discountPriceTotalLimitAmountRadioValue],
		);

		return (
			<FormFieldLayout title="合計上限金額">
				<RadioGroupField
					value={discountPriceTotalLimitAmountRadioValue}
					options={Constants.DiscountPriceTotalLimitOptions}
					handleValueChange={handleChangeDiscountPriceTotalLimit}
				/>
				{canShowDiscountPriceTotalLimitAmount && (
					<>
						<div className="mt-3 flex items-center gap-x-3">
							<p>総額</p>

							<InputNumberField
								handleInput={handleChangeDiscountPriceTotalLimitAmount}
								value={discountPriceTotalLimitAmount?.value}
								errorMessage={discountPriceTotalLimitAmount?.error}
							/>
							<p>円（税込）引きまで可能</p>
						</div>

						<div className="mt-2">
							<p className="whitespace-pre-wrap text-sm text-caption-50">
								{`予約が短時間の間に集中して実行された場合、制限金額を超えて予約を受け付けてしまう可能性があります。\nまた、特定のご予約で上限金額を超える場合、最初に上限金額を超えるご予約までディスカウントが適用されます。\n最初に上限金額を超えた後にこのディスカウントが適用不可能になります。\nあくまで簡易的な制限としてご利用ください。制限金額を超えた場合の保証はできかねます。`}
							</p>
						</div>
					</>
				)}
			</FormFieldLayout>
		);
	},
);
